import './SectionsList.scss';

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Breadcrumb, Button, Col, Container, Row, Card, Form, Badge } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import AppSearch from '../../../components/common/AppSearch'
import { ControlPanelManager, SectionListDTO } from '../../../api/ControlPanelManager';
import { useToasts } from '../../../contexts/toasts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Checkbox, CheckboxChangeEvent, Flex, Input, Switch, Tooltip } from 'antd';
import { SectionKey } from '../../../components/layout/AppSidebar';
import IconButton from '../../../components/common/IconButton';
import { cloneDeep, set } from 'lodash';
import classNames from 'classnames';
import { useFullScreenLoader } from '../../../contexts/fullScreenLoader';
import { sectionIconsMap } from '../../../utils';
import { updateSections } from '../../../slices/sectionsSlice';
import { useDispatch } from 'react-redux';
import AppLoader from '../../../components/common/AppLoader';


export default function SectionsList() {
  const [searchText, setSearchText] = useState('');
  const [sections, setSections] = useState<SectionListDTO[]>();
  const [error, setError] = useState(false);
  const dispatch = useDispatch();

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();

  const filteredSections = useMemo(() => {
    return sections
      ?.sort((a, b) => a.order! - b.order!)
      .filter(s =>
        s.key.toLowerCase().includes(searchText.toLowerCase()) ||
        s.items?.some(item => item.key.toLowerCase().includes(searchText.toLowerCase()))
      )
  }, [sections, searchText]);

  const initSections = useCallback(() => {
    ControlPanelManager.getSections()
      .then(
        ({ data }) => setSections((data as Array<SectionListDTO>)),
        (err) => {
          setError(true);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('notifications-error', message, `Notifications Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [showToast]);

  const reset = useCallback(() => {
    setSections(undefined);
    initSections();
  }, [initSections]);

  useEffect(reset, [reset]);

  const handleSubmit = useCallback(() => {
    showLoader();
    const body = sections!.map((s, index) => ({ ...s, order: index + 1 }));

    ControlPanelManager.updateSections(body)
      .then(
        ({ data }) => {
          showToast('update-sections-success', 'Sections Updated Successfully', null, 'success');
          hideLoader();
          reset();
          dispatch(updateSections(data));
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('sections-error', message, `Sections Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [reset, showLoader, hideLoader, showToast, sections, dispatch]);

  const handleAddSection = useCallback(() => {
    setSections(curr => curr!.concat({ key: '' as SectionKey, labels: { title: 'New Section' }, order: curr!.length }));
    window.scrollTo({ top: 600, behavior: 'smooth' });
  }, []);

  const handleRemoveSection = useCallback((index: number) => () => {
    setSections(curr => {
      const newSections = [...curr!];
      newSections.splice(index, 1);
      return newSections;
    });
  }, []);

  const handleAddSubSection = useCallback((index: number) => () => {
    setSections(curr => {
      const newSections = [...curr!];
      if (!newSections[index].items) newSections[index].items = [];
      newSections[index].items!.push({ key: "" as SectionKey, labels: { title: 'New Section' } });
      return newSections;
    });
  }, []);

  const handleRemoveSubSection = useCallback((i: number, j: number) => () => {
    setSections(curr => {
      const newSections = [...curr!];
      newSections[i].items!.splice(j, 1);
      return newSections;
    });
  }, []);

  const handleFieldChange = useCallback((evt: React.ChangeEvent<HTMLInputElement>) => {
    const path = evt.target.name, value = evt.target.value;

    setSections(curr => {
      const newSections = [...curr!];
      set(newSections, path, value);
      return newSections;
    });
  }, []);

  const toggleSectionAdmin = useCallback((evt: CheckboxChangeEvent) => {
    const path = evt.target.name, value = evt.target.checked;

    setSections(curr => {
      const newSections = [...curr!];
      set(newSections, path!, value);
      return newSections;
    });
  }, []);

  const toggleSectionVisibility = useCallback((index: number) => (show: boolean) => {
    setSections(curr => {
      const newSections = [...curr!];
      newSections[index].hide = !show;
      return newSections;
    })
  }, []);

  const moveUp = useCallback((order: number) => () => {
    setSections(curr => {
      const newSections = cloneDeep(curr!);
      newSections.forEach(s => {
        if (s.order === order) s.order -= 1;
        else if (s.order === order - 1) s.order = order;
      })
      return newSections;
    });
  }, []);

  const moveDown = useCallback((order: number) => () => {
    setSections(curr => {
      const newSections = cloneDeep(curr!);
      newSections.forEach(s => {
        if (s.order === order) s.order += 1;
        else if (s.order === order + 1) s.order = order;
      })
      return newSections;
    });
  }, []);

  const renderAsyncContent = useCallback(() => {
    if (error) return (
      <Alert variant="danger">
        <FontAwesomeIcon icon={solid('exclamation-circle')} />
        <span className="ms-2">An error occurred, try again later.</span>
      </Alert>
    );

    if (!filteredSections) return <AppLoader className="py-5 my-5" justify="center" size={100} />

    return (
      <Flex vertical>
        {filteredSections.map((section, index) => (
          <Card key={index} className={classNames("section-item p-2", { hidden: section.hide })}>
            <Row className="align-items-center mb-2 gx-3">
              <Col md={3} className="d-flex align-items-center">
                <Tooltip title="Order">
                  <Badge className="me-3">#{section.order || 0 + 1}</Badge>
                </Tooltip>
                <Form.Group className="w-100">
                  <Input placeholder="Section key" name={`${index}.key`} prefix={<FontAwesomeIcon className="me-1" icon={sectionIconsMap[section.key] || solid('icons')} fixedWidth />} value={section.key} onChange={handleFieldChange} />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Input placeholder="Section URL" name={`${index}.url`} value={section.url} onChange={handleFieldChange} />
                </Form.Group>
              </Col>
              <Col md={2} className="ms-auto">
                <Checkbox name={`${index}.admin`} checked={section.admin} onChange={toggleSectionAdmin}>
                  <FontAwesomeIcon icon={solid('crown')} className={classNames({ 'text-warning': section.admin })} />
                  <span className="ms-2">Admin</span>
                </Checkbox>
              </Col>
              <Col md={2}>
                <Flex align="center">
                  <Switch size="small" id={`section-toggle-${index}`} checked={!section.hide} onChange={toggleSectionVisibility(index)} />
                  <label htmlFor={`section-toggle-${index}`} className="ms-2 action">Show Section</label>
                </Flex>
              </Col>
              <Col md={2} className="d-flex align-items-center justify-content-end">
                <IconButton className="p-1 me-2" tooltip="Move up" disabled={index === 0} onClick={moveUp(section.order!)}>
                  <FontAwesomeIcon icon={solid('arrow-up')} />
                </IconButton>
                <IconButton className="p-1 me-2" tooltip="Move down" disabled={index === filteredSections.length - 1} onClick={moveDown(section.order!)}>
                  <FontAwesomeIcon icon={solid('arrow-down')} />
                </IconButton>
                <Button variant="light" size="sm" className="ms-2 border text-danger" onClick={handleRemoveSection(index)}>
                  <FontAwesomeIcon icon={solid('trash')} />
                  <span className="ms-2">Remove Section</span>
                </Button>
              </Col>
            </Row>

            <Row className="mb-2 gx-3">
              <Col md={3}>
                <Form.Group as={Flex} align="center">
                  <Form.Label className="pe-2 me-2">Title</Form.Label>
                  <Input placeholder="Section Title" name={`${index}.labels.title`} value={section.labels.title} onChange={handleFieldChange} />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group as={Flex} align="center">
                  <Form.Label className="me-2">Singular</Form.Label>
                  <Input placeholder="Section Singular" name={`${index}.labels.singular`} value={section.labels.singular} onChange={handleFieldChange} />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group as={Flex} align="center">
                  <Form.Label className="me-2">Plural</Form.Label>
                  <Input placeholder="Section Plural" name={`${index}.labels.plural`} value={section.labels.plural} onChange={handleFieldChange} />
                </Form.Group>
              </Col>
            </Row>

            <Flex align="center" justify="space-between" className="mb-1">
              <b className="text-muted">Sub-Sections</b>
              <Button variant="light" className="border" size="sm" onClick={handleAddSubSection(index)}>
                <FontAwesomeIcon icon={solid('plus')} />
                <span className="ms-2">Add Sub-section</span>
              </Button>
            </Flex>

            {(!section.items || section.items.length === 0)
              ? (
                <Alert variant="info" className="p-1 px-2">
                  <FontAwesomeIcon icon={solid('info-circle')} />
                  <span className="ms-2">No sub-sections found.</span>
                </Alert>
              )
              : (
                <ul className="section-items">
                  {section.items?.map((subSection, j) => (
                    <li className="section-item" key={j}>
                      <Flex align="center" gap={20}>
                        <Form.Group>
                          <Input placeholder="Sub-Section Key" name={`${index}.items.${j}.key`} prefix={<FontAwesomeIcon className="me-1" icon={sectionIconsMap[subSection.key] || solid('icons')} fixedWidth />} value={subSection.key} onChange={handleFieldChange} />
                        </Form.Group>
                        <Form.Group>
                          <Input placeholder="Sub-Section URL" name={`${index}.items.${j}.url`} value={subSection.url} onChange={handleFieldChange} />
                        </Form.Group>
                        <Form.Group as={Flex} align="center">
                          <Form.Label className="me-2">Title</Form.Label>
                          <Input placeholder="Sub-section Title" name={`${index}.items.${j}.labels.title`} value={subSection.labels.title} onChange={handleFieldChange} />
                        </Form.Group>
                        <Form.Group as={Flex} align="center">
                          <Form.Label className="me-2">Singular</Form.Label>
                          <Input placeholder="Sub-section Singular" name={`${index}.items.${j}.labels.singular`} value={subSection.labels.singular} onChange={handleFieldChange} />
                        </Form.Group>
                        <Form.Group as={Flex} align="center">
                          <Form.Label className="me-2">Plural</Form.Label>
                          <Input placeholder="Sub-section Plural" name={`${index}.items.${j}.labels.plural`} value={subSection.labels.plural} onChange={handleFieldChange} />
                        </Form.Group>

                        <IconButton className="ms-auto text-danger p-1" onClick={handleRemoveSubSection(index, j)}>
                          <FontAwesomeIcon icon={solid('times')} />
                          <span className="ms-2">Remove</span>
                        </IconButton>
                      </Flex>
                    </li>
                  ))}
                </ul>
              )
            }
          </Card>
        ))}
      </Flex>
    );
  }, [filteredSections, error, handleFieldChange, handleRemoveSection, handleAddSubSection, handleRemoveSubSection, toggleSectionAdmin, toggleSectionVisibility, moveUp, moveDown]);

  return (
    <Container className="my-3">
      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/control-panel" }}>Control Panel</Breadcrumb.Item>
        <Breadcrumb.Item active>Sections Management</Breadcrumb.Item>
      </Breadcrumb>

      <Row className="mb-3">
        <Col className="text-center px-3">
          <AppSearch placeholder="Search section..." value={searchText} onChange={setSearchText} />
        </Col>

        <Col className="text-end w-auto">
          <Button size="sm" variant="light" className="border me-2" onClick={handleAddSection} disabled={!sections}>
            <FontAwesomeIcon icon={solid('plus')} />
            <span className="ms-2">Add Section</span>
          </Button>
          <Button size="sm" variant="success" disabled={!sections} onClick={handleSubmit}>
            <FontAwesomeIcon icon={solid('save')} />
            <span className="ms-2">Save Sections</span>
          </Button>
        </Col>
      </Row>

      {renderAsyncContent()}
    </Container>
  )
}